import {
  DragSourceMonitor,
  useDrag,
  useDragDropManager,
  useDrop,
} from 'react-dnd';
import { Identifier } from 'dnd-core';
import { config } from 'config';
import { ReactComponent as FolderIcon } from 'resources/images/folder.svg';
import { ReactComponent as FolderSolidIcon } from 'resources/images/folder-solid.svg';
import { SONG_DRAG_EVENT_TYPE } from 'app/components/Song/SongList/constants';
import { ContextMenuOption } from 'types/general.types';
import { FolderItem } from 'types/song';
import {
  orderFolderEffect,
  updateFolderEffect,
} from 'app/store/SongList/effects';
import { useAppDispatch } from 'app/store';
import { SongDragItem } from 'app/components/Song/SongList/types';
import { useState } from 'react';
import { PIN_DRAG_EVENT_TYPE } from 'app/components/PinnedSongsSection/constants';
import { SiderBarLink, SiderBarLinkProps } from '../SiderBarLink';
import { getStyles } from './styles';
import { FOLDER_DRAG_EVENT_TYPE } from './constants';

interface SongFolderProps
  extends Omit<SiderBarLinkProps, 'icon' | 'hoverIcon'> {
  guid: string;
  setOptions: React.Dispatch<React.SetStateAction<ContextMenuOption[] | null>>;
  setPosition: React.Dispatch<
    React.SetStateAction<{ x: number; y: number } | null>
  >;
  setRenameSongFolder: React.Dispatch<React.SetStateAction<FolderItem | null>>;
  setRemoveSongFolder: React.Dispatch<React.SetStateAction<FolderItem | null>>;
}

export const SongFolder = ({
  guid,
  setOptions,
  setPosition,
  setRenameSongFolder,
  setRemoveSongFolder,
  ...rest
}: SongFolderProps) => {
  const dispatch = useAppDispatch();
  const dragDropManager = useDragDropManager();
  const offset =
    dragDropManager.getMonitor().getDifferenceFromInitialOffset()?.y || 0;

  const [{ isOver, dragType }, drop] = useDrop<
    SongDragItem,
    unknown,
    {
      isOver: boolean;
      dragType: Identifier | null;
    }
  >(() => ({
    accept: [SONG_DRAG_EVENT_TYPE, FOLDER_DRAG_EVENT_TYPE, PIN_DRAG_EVENT_TYPE],
    drop({ songGuids, guid: folderGuid }, monitor) {
      const type = monitor.getItemType();

      if (type === PIN_DRAG_EVENT_TYPE) {
        dispatch(
          updateFolderEffect({
            guid,
            addSongs: [folderGuid],
          })
        );
      }

      if (type === SONG_DRAG_EVENT_TYPE) {
        dispatch(
          updateFolderEffect({
            guid,
            addSongs: songGuids,
          })
        );
      }

      if (type === FOLDER_DRAG_EVENT_TYPE && folderGuid !== guid) {
        const isDraggingUp =
          (monitor.getDifferenceFromInitialOffset()?.y || 0) < 0;
        dispatch(
          orderFolderEffect({
            folderGuid,
            targetFolderGuid: guid,
            before: isDraggingUp,
          })
        );
      }
    },
    collect(monitor) {
      return {
        isOver: monitor.isOver({ shallow: true }),
        dragType: monitor.getItemType(),
      };
    },
  }));

  const [{ isDraggingCurrentFolder }, drag] = useDrag({
    type: FOLDER_DRAG_EVENT_TYPE,
    item: { guid },
    collect: (monitor: DragSourceMonitor) => ({
      isDraggingCurrentFolder: monitor.isDragging(),
    }),
  });

  const isDraggingAnyFolder = dragType === FOLDER_DRAG_EVENT_TYPE;

  const [isHovered, setIsHovered] = useState(false);
  const { rootSx } = getStyles(
    isOver,
    isHovered,
    isDraggingAnyFolder,
    isDraggingCurrentFolder,
    offset
  );

  const options: ContextMenuOption[] = [
    {
      title: 'Rename',
      value: 'RENAME',
      onClick: () => {
        setRenameSongFolder({ guid, title: rest.label });
      },
    },
    {
      title: 'Remove',
      value: 'REMOVE',
      onClick: () => {
        setRemoveSongFolder({ guid, title: rest.label });
      },
    },
  ];

  const attachRef = (ref: HTMLAnchorElement) => {
    drag(ref);
    drop(ref);
  };

  return (
    <SiderBarLink
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        if (config.isElectronBuild && (e.shiftKey || e.ctrlKey || e.metaKey)) {
          e.preventDefault();
        }
      }}
      ref={attachRef}
      sx={rootSx}
      icon={FolderIcon}
      activeIcon={FolderSolidIcon}
      onContextMenu={(e) => {
        e.preventDefault();
        setOptions(options);
        const {
          x,
          y,
          width,
        } = (e.target as HTMLElement)?.getBoundingClientRect();
        setPosition({ x: x + width, y: y + 10 });
      }}
      {...rest}
    />
  );
};
